import Modal from "common/Modal/Modal";
import Flex from "common/Boxes/Flex";
import CopyOutlined from "@ant-design/icons/CopyOutlined";
import styled from "styled-components";
import { copyImgToClipboard, getDownloadImageUrl } from "utils/misc";
import React, { useEffect, useState } from "react";
import { Download } from "common/Icons/Icons";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import Button from "common/Buttons/Button";
import Link from "common/Typography/Link";
import useMedia from "utils/hooks/useMedia";
import { IS_TELEGRAM_WEB_APP, TELEGRAM_WEB_APP } from "common/constants.ts";

interface ShareImageModalProps {
  visible: boolean;
  closeModal: () => void;
  imageUrl?: string;
  imageTitle?: string;
}

const ShareImageModal = ({
  visible,
  closeModal,
  imageUrl,
  imageTitle,
}: ShareImageModalProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const isMobile = useMedia([`(max-width: 550px)`], [true], false);
  const [downloadUrl, setDownloadUrl] = useState<string>();
  const fileName = `${imageTitle}.png`;

  useEffect(() => {
    if (imageUrl && !IS_TELEGRAM_WEB_APP)
      getDownloadImageUrl(imageUrl).then((url) => setDownloadUrl(url));
  }, [imageUrl]);

  const onClose = () => {
    setDownloadUrl(undefined);
    setIsLoading(false);
    closeModal();
  };

  const telegramDownload = () => {
    try {
      TELEGRAM_WEB_APP?.downloadFile({
        file_name: fileName,
        url: imageUrl,
      });
    } catch {}
  };

  const getDownloadButtonProps = () => {
    if (IS_TELEGRAM_WEB_APP) {
      return {
        onClick: telegramDownload,
      };
    } else {
      return {
        href: downloadUrl,
        download: fileName,
      };
    }
  };

  const buttons = (
    <ButtonsWrapper $isMobile={isMobile}>
      <CancelButton $isMobile={isMobile} onClick={closeModal}>
        Cancel
      </CancelButton>
      {!IS_TELEGRAM_WEB_APP && (
        <ImageActionButton
          type="primary"
          $isMobile={isMobile}
          onClick={() => copyImgToClipboard(imageUrl)}
        >
          <CopyIcon />
          Copy
        </ImageActionButton>
      )}
      <ImageActionButton
        type="primary"
        $isMobile={isMobile}
        {...getDownloadButtonProps()}
      >
        <DownloadIcon />
        Download
      </ImageActionButton>
    </ButtonsWrapper>
  );

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      title={false}
      footer={false}
      closable={false}
      width={520}
      centered={true}
      styles={{
        body: { padding: 0, minHeight: isMobile ? 230 : 355 },
      }}
    >
      <LoadingSpinnerOverlay
        isLoading={isLoading}
        wrap={true}
        hideContentWhileLoading={true}
      >
        <ImagePreview
          src={imageUrl}
          $isMobile={isMobile}
          onLoad={() => setIsLoading(false)}
          key={imageUrl}
        />
      </LoadingSpinnerOverlay>
      {buttons}
    </Modal>
  );
};

export default ShareImageModal;

const ImagePreview = styled.img<{ $isMobile: boolean }>`
  border-radius: 8px 8px 0 0;
  width: 100%;
  min-height: ${(p) => (p.$isMobile ? 170 : 290)}px;
`;
const ButtonsWrapper = styled(Flex)<{ $isMobile: boolean }>`
  grid-gap: 8px;
  justify-content: flex-end;
  padding: ${(p) => (p.$isMobile ? 12 : 16)}px;
`;
const ImageActionButton = styled(Button)<{ $isMobile: boolean }>`
  fill: ${(p) => p.theme.colors.white};
  ${(p) =>
    p.$isMobile &&
    `
  padding-right: 10px;
  padding-left: 10px;
  `}
`;
const CancelButton = styled(Link)<{ $isMobile: boolean }>`
  margin-right: ${(p) => (p.$isMobile ? 4 : 12)}px;
`;
const DownloadIcon = styled(Download)`
  top: 3px;
  margin-right: 8px;
  position: relative;
  fill: ${(p) => p.theme.colors.buttonText};
`;
const CopyIcon = styled(CopyOutlined)`
  position: relative;
  top: 1px;
`;
